import {
  ApplicationConfig,
  importProvidersFrom,
  PLATFORM_ID,
  Provider,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import {
  provideHttpClient,
  withFetch,
  withInterceptors,
} from '@angular/common/http';
import { TranslateModule } from '@ngx-translate/core';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getAuth, provideAuth } from '@angular/fire/auth';

import {
  AUTH_SERVICE,
  FirebaseAuthenticationService,
} from '@dibsondance/shared-authentication-base';
import { API_URL_TOKEN, I18N_HELPER_BASE } from '@dibsondance/shared-rest-client-base';
import { I18nHelper } from '@dibsondance/shared-translations';
import { LoggingModule } from '@dibsondance/shared-logging';
import {
  ENVIRONMENT_SERVICE,
  EnvironmentService,
} from '@dibsondance/shared-environment-base';
import {
  MaterialConfigHostModule,
  provideMaterialConfigHost,
} from '@dibsondance/material-config-host';
import {
  HOST_AUTH_SERVICE,
  HostAuthenticationService,
  hostAuthInterceptor,
} from '@dibsondance/authentication-host';
import {
  BROWSER_SERVICE,
  browserServiceFactory,
  WINDOW_OBJ,
  WINDOW_OBJ_PROVIDER,
} from '@dibsondance/shared-browser-base';

import { environment } from '../environment/environment';
import { AuthGuard } from './guards';
import { routes } from './app.routes';
import { firebaseConfig } from './app.fire';

const ENVIRONMENT_SERVICE_PROVIDER: Provider = {
  provide: ENVIRONMENT_SERVICE,
  useFactory: () =>
    new EnvironmentService({
      apiUrl: environment.apiUrl,
      hostUrl: environment.hostUrl,
      joinProUrl: environment.joinProUrl,
      portalUrl: environment.portalUrl,
      buildTimestamp: environment.buildTimestamp,
      buildUnixTime: environment.buildTimestamp,
      production: environment.production,
      buildNumber: 'DEV-LOCAL',
      googleMapsApiKey: environment.googleMapsApiKey,
      geocodingApiKey: environment.geocodingApiKey,
      // NOTE: this factory copies the environment field by field, so anything
      // added to environment*.ts must be listed here too or it silently
      // disappears at runtime while still type-checking.
      scanner: environment.scanner,
    }),
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideRouter(routes),
    provideHttpClient(withInterceptors([hostAuthInterceptor]), withFetch()),
    provideFirebaseApp(() => initializeApp(firebaseConfig)),
    provideAuth(() => getAuth()),
    WINDOW_OBJ_PROVIDER,
    {
      provide: AUTH_SERVICE,
      useClass: FirebaseAuthenticationService,
    },
    {
      provide: HOST_AUTH_SERVICE,
      useClass: HostAuthenticationService,
    },
    {
      provide: BROWSER_SERVICE,
      useFactory: browserServiceFactory,
      deps: [PLATFORM_ID, WINDOW_OBJ],
    },
    // Why do wee need it here - AuthGuard
    AuthGuard,
    {
      provide: API_URL_TOKEN,
      useFactory: () =>
        environment.apiUrl ? `${environment.apiUrl}api/pro` : '/api/pro',
    },
    {
      provide: I18N_HELPER_BASE,
      useExisting: I18nHelper,
    },
    I18nHelper,
    ENVIRONMENT_SERVICE_PROVIDER,
    importProvidersFrom(TranslateModule.forRoot()),
    // Why do we need to import material module and then provide config
    importProvidersFrom(MaterialConfigHostModule),
    ...provideMaterialConfigHost(I18nHelper),
    importProvidersFrom(
      LoggingModule.forRoot({
        environment: environment.production ? 'production' : 'development',
        enableConsole: true,
        enableHttp: false,
        enableLocalStorage: false,
        httpEndpoint: '/api/logs',
        enableInterceptor: false,
      })
    ),
  ],
};
